// ===========================================
// Messages React Query Hooks
// ===========================================
// Custom hooks for messaging functionality

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  messagesApi,
  SendMessageInput,
  GetMessagesParams,
} from '../api/messages.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const messageKeys = {
  all: ['messages'] as const,
  conversations: () => [...messageKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...messageKeys.all, 'conversation', id] as const,
  unreadCount: () => [...messageKeys.all, 'unread-count'] as const,
  recipients: () => [...messageKeys.all, 'recipients'] as const,
};

// ===========================================
// CONVERSATION QUERIES
// ===========================================

/**
 * Get all conversations for the current user
 */
export function useConversations() {
  return useQuery({
    queryKey: messageKeys.conversations(),
    queryFn: () => messagesApi.getConversations(),
  });
}

/**
 * Get messages for a specific conversation
 * Refetches every 5 seconds when enabled
 */
export function useConversationMessages(
  conversationId: string | null,
  params?: GetMessagesParams,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: messageKeys.conversation(conversationId || ''),
    queryFn: () => messagesApi.getConversationMessages(conversationId!, params),
    enabled: !!conversationId && (options?.enabled !== false),
    refetchInterval: options?.refetchInterval ?? 5000, // Refresh every 5 seconds by default
  });
}

/**
 * Get unread message count
 * Refetches every 30 seconds
 */
export function useUnreadCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: messageKeys.unreadCount(),
    queryFn: () => messagesApi.getUnreadCount(),
    enabled: options?.enabled !== false,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Get list of valid recipients
 */
export function useRecipients() {
  return useQuery({
    queryKey: messageKeys.recipients(),
    queryFn: () => messagesApi.getRecipients(),
  });
}

// ===========================================
// MESSAGE MUTATIONS
// ===========================================

/**
 * Broadcast a message to class members (bypasses general permission check).
 * Used by the "Message Class" feature on the lesson detail page.
 */
export function useClassBroadcast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { lessonId: string; recipientIds: string[]; content: string }) =>
      messagesApi.classBroadcast(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
    },
  });
}

/**
 * Broadcast a message to members of one family.
 * Used by "Message family" on the admin Families page.
 */
export function useFamilyBroadcast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { familyId: string; recipientIds: string[]; content: string }) =>
      messagesApi.familyBroadcast(data),
    onSuccess: () => {
      // The sender's own inbox now holds these conversations too.
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
    },
  });
}

/**
 * Send a new message
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: SendMessageInput) => messagesApi.sendMessage(data),
    onSuccess: () => {
      // Invalidate conversations list (updates last message, unread counts)
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      // Invalidate unread count
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
      enqueueSnackbar('Message sent', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to send message';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mark conversation as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => messagesApi.markAsRead(conversationId),
    onSuccess: () => {
      // Invalidate conversations list (updates unread counts)
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      // Invalidate unread count
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      console.error('Failed to mark conversation as read:', error);
    },
  });
}

// ===========================================
// UTILITY HOOKS
// ===========================================

/**
 * Invalidate all message-related queries
 * Useful when conversation changes or new message arrives
 */
export function useInvalidateMessages() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: messageKeys.all });
  };
}

/**
 * Prefetch conversation messages
 * Useful for preloading when hovering over a conversation
 */
export function usePrefetchConversation() {
  const queryClient = useQueryClient();

  return (conversationId: string) => {
    queryClient.prefetchQuery({
      queryKey: messageKeys.conversation(conversationId),
      queryFn: () => messagesApi.getConversationMessages(conversationId),
    });
  };
}
